import { Box, Button, Typography } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import type { IqeProject, TestRun } from '../../interfaces/ui';
import TestRunSummary from './TestRunSummary';
import TestExecutionHistory from './TestExecutionHistory';
import TestExecutionEmptyState from './TestExecutionEmptyState';
import { getProjectExecutionActionState } from '../../utils/projectExecutionLifecycle';
import type { TestExecutionActionKey } from '../../utils/projectExecutionLifecycle';
import ThreeDotIndicator from '../../common/ThreeDotIndicator';
import { isProjectOperationInProgress } from '../../utils/projectStatusChecks';

interface RunHistoryTabProps {
  apmNumber: string;
  project: IqeProject | null;
  runs: TestRun[];
  runsLoading?: boolean;
  runsError?: string | null;
  onRetry?: () => void;
  isToolAdmin?: boolean;
}

export default function RunHistoryTab({
  apmNumber,
  project,
  runs,
  runsLoading = false,
  runsError = null,
  onRetry,
  isToolAdmin = false,
}: RunHistoryTabProps) {
  if (!project) return null;

  // First load with nothing to show yet — avoid flashing empty state before data arrives.
  if (runsLoading && runs.length === 0) {
    return (
      <Box className="ec-run-history-tab ec-activity-waiting">
        <ThreeDotIndicator />
        <Typography className="ec-activity-waiting-text">
          Loading run history...
        </Typography>
      </Box>
    );
  }

  const hasTestAssets = !!(runs && runs.length > 0);
  const isExecutionInProgress = isProjectOperationInProgress(project.status);

  const { displayVersion, options } = getProjectExecutionActionState(
    runs,
    isExecutionInProgress,
  );

  const handleActionSelect = (action: TestExecutionActionKey) => {
    // Action selection would typically trigger state changes or navigation
    // In the ExecutionCenter context, these are handled by parent
    console.log('Action selected:', action);
  };

  const handleViewCurrentStatus = () => {
    // Navigation to activity tab with current mode
    console.log('View current status');
  };

  return (
    <Box className="ec-run-history-tab">
      {runsError && (
        <Box
          className="pd-table-status-badge pd-table-status-badge-failed pd-runs-error"
          role="alert"
        >
          <WarningIcon />
          <Typography className="pd-runs-error-message">
            Run history is temporarily unavailable.
          </Typography>
          {onRetry && (
            <Button
              className="pd-runs-retry-btn"
              onClick={onRetry}
              disabled={runsLoading}
              size="small"
            >
              {runsLoading ? 'Retrying...' : 'Retry'}
            </Button>
          )}
        </Box>
      )}

      {/* Latest Run Summary */}
      {hasTestAssets && (
        <TestRunSummary
          currentVersion={displayVersion}
          actionOptions={options}
          actionDisabled={!isToolAdmin}
          isExecutionInProgress={isExecutionInProgress}
          onViewCurrentStatus={handleViewCurrentStatus}
          onActionSelect={handleActionSelect}
          showActions={false}
        />
      )}

      {/* Execution History Table or Empty State */}
      {!hasTestAssets ? (
        <TestExecutionEmptyState
          project={project}
          isToolAdmin={isToolAdmin}
          onGenerateTests={() => {
            console.log('Generate tests');
          }}
          showAction={false}
        />
      ) : (
        <TestExecutionHistory
          apmNumber={apmNumber}
          project={project}
          runs={runs}
          onViewResults={() => {
            console.log('View results');
          }}
        />
      )}
    </Box>
  );
}
